import { Component, OnInit, ViewChild, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { ErrorRule } from '../models/error-rule';
import { ActivatedRoute, Router } from '@angular/router';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { NgForm, FormsModule } from '@angular/forms';
import { ErrorRuleCondition } from '../models/error-rule-condition';
import { ErrorRulesService } from '../services/error-rules.service';
import { ErrorRuleActions } from '../models/error-rule-actions';
import { PartnerContactService } from 'src/app/partner/services/partner-contact.service';
import { PartnerContactType } from 'src/app/partner/models';
import { computedAsync } from 'src/app/shared/signal-utilities';
import { RulePosition } from '../models/error-rule-position';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { LayoutContainerComponent } from '../../controls/components/basic/layout-container/layout-container.component';
import { ContainerFluidDirective } from '../../controls/directives/container-fluid.directive';
import { LayoutRowComponent } from '../../controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from '../../controls/components/basic/layout-column/layout-column.component';
import { TextboxComponent } from '../../controls/components/basic/textbox/textbox.component';
import { ErrorRulesConditionsComponent } from '../components/error-rules-conditions/error-rules-conditions.component';
import { ErrorRulesActionsComponent } from '../components/error-rules-actions/error-rules-actions.component';
import { CheckboxComponent } from '../../controls/components/basic/checkbox/checkbox.component';
import { ButtonComponent } from '../../controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from '../../controls/directives/button-primary.directive';
import { ButtonSubmitDirective } from '../../controls/directives/button-submit.directive';
import { ButtonSecondaryDirective } from '../../controls/directives/button-secondary.directive';

enum Mode { Edit, New }

@Component({
    selector: 'app-error-rules-details',
    templateUrl: './error-rules-details.component.html',
    styleUrls: ['./error-rules-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, LayoutContainerComponent, ContainerFluidDirective, LayoutRowComponent, LayoutColumnComponent, TextboxComponent, ErrorRulesConditionsComponent, ErrorRulesActionsComponent, CheckboxComponent, ButtonComponent, ButtonPrimaryDirective, ButtonSubmitDirective, ButtonSecondaryDirective]
})
export class ErrorRulesDetailsComponent implements OnInit {
  @ViewChild('tForm')
  private form: NgForm;

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private stakeholderService = inject(StakeholderService);
  private errorRuleService = inject(ErrorRulesService);
  private partnerContactService = inject(PartnerContactService);
  private loadingPanelService = inject(LoadingPanelService);
  private toastService = inject(ToastService);
  errorRuleId = input.required<number>();

  stakeholders = signal<Stakeholder[]>([]);
  contactTypes = signal<PartnerContactType[]>([]);
  errorRules = signal<ErrorRule[]>([]);
  mode = signal(Mode.Edit);

  conditionSummary = signal("");
  actionSummary = signal("");
  summary = computed(() => {
    var conditions = this.conditionSummary()
    var action = this.actionSummary()
    if (action != "" && conditions != "") {
      action = "\nThen " + action;
    }
    return conditions + action;
  });

  errorRule = computedAsync(async () => {
    const id = this.errorRuleId();
    if (id == 0) {
      this.mode.set(Mode.New);
      return this.getNewErrorRule();
    }
    else {
      var rule = await this.errorRuleService.getErrorRule(id);
      rule.Conditions.map(c => {
        c.Values = c.Values.map(v => {
          return {id: Math.floor((Math.random() * 100000) + 1), value: v }
        })
      })
      return rule;
    }
  }, this.getNewErrorRule());

  isNewRule = computed(() => {
    return this.mode() === Mode.New
  });

  isDefaultRule = computed(() => {
    const id = this.errorRuleId();
    const lastErrorRule = this.errorRules()?.slice(-1)[0];
    return lastErrorRule?.Id == id;
  });

  actions = computed(() => {
    return {
      NotifyStakeholder: this.errorRule().NotifyStakeholder,
      NotifyStakeholderUrgent: this.errorRule().NotifyStakeholderUrgent,
      NotifySenderPartnerContactTypeId: this.errorRule().NotifySenderPartnerContactTypeId,
      NotifyReceiverPartnerContactTypeId: this.errorRule().NotifyReceiverPartnerContactTypeId,
      ReassignToErrorCode: this.errorRule().ReassignToErrorCode,
      SetIgnoredStatus: this.errorRule().SetIgnoredStatus
    } as ErrorRuleActions
  });

  async ngOnInit(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');
      const [stakeholders, errorRules, contactTypes] = await Promise.all([
        this.stakeholderService.getStakeholders(),
        this.errorRuleService.getErrorRules(),
        this.partnerContactService.getContactTypes()
      ]);
      this.stakeholders.set(stakeholders);
      this.errorRules.set(errorRules);
      this.contactTypes.set(contactTypes);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  getNewErrorRule(): ErrorRule {
    return {
      Id: null,
      SortOrder: -1,
      Hits: 0,
      LastRun: null,
      Name: null,
      Description: null,
      StakeholderId: null,
      ConditionSummary: null,
      ActionSummary: null,
      Action: null,
      IsEnabled: true,
      Conditions: [],
      NotifyStakeholder: false,
      NotifyStakeholderUrgent: false,
      NotifySenderPartnerContactTypeId: null,
      NotifyReceiverPartnerContactTypeId: null,
      ReassignToErrorCode: null,
      SetIgnoredStatus: false,
      CreatedBy: 0,
      CreatedDate: new Date(),
      UpdatedBy: null,
      UpdatedDate: null
    }
  }

  onConditionSummaryChange(summary: string) {
    this.conditionSummary.set(summary);
  }

  onActionSummaryChange(summary: string) {
    this.actionSummary.set(summary);
  }

  async formSubmit(): Promise<void> {
    const rulePosition = this.mode() === Mode.New ? RulePosition.last : RulePosition.current;

    const payload: ErrorRule = {
      ...this.errorRule(),
      Name: this.form.value.name,
      Description: this.form.value.description,
      Action: this.form.value.action,
      IsEnabled: this.isDefaultRule() ? true : this.form.value.isEnabled,
      Conditions: this.convertConditionsObjectToArray(this.form.value.conditions),
      StakeholderId: this.form.value.actions.stakeholderId,
      NotifyStakeholder: this.form.value.actions.notifyStakeholder,
      NotifyStakeholderUrgent: this.form.value.actions.notifyStakeholderUrgent,
      NotifySenderPartnerContactTypeId: this.form.value.actions.notifySenderPartnerContactTypeId,
      NotifyReceiverPartnerContactTypeId: this.form.value.actions.notifyReceiverPartnerContactTypeId,
      SetIgnoredStatus: this.form.value.actions.setIgnoredStatus,
      ReassignToErrorCode: this.form.value.actions.reassignToErrorCode,
      ConditionSummary: this.conditionSummary(),
      ActionSummary: this.actionSummary()
    };

    try {
      const savedErrorRule = await this.errorRuleService.saveErrorRule(payload, rulePosition);
      const savedStatus = savedErrorRule.IsEnabled ? 'Active' : 'Inactive';
      this.toastService.showToast('Error rule is now saved and set to status ' + savedStatus, ToastType.success);
      void this.router.navigate(['..'], { relativeTo: this.activatedRoute });
    }
    catch {
      this.toastService.showToast('Failed to save error rule.', ToastType.error);
    }
  }

  formCancel() {
    void this.router.navigate(['..'], { relativeTo: this.activatedRoute });
  }

  convertConditionsObjectToArray(obj: any): ErrorRuleCondition[]  {
    if (obj) {
      let conditionsArr = Object.entries(obj).map((key) => {
        let condition = key[1];
        condition['id'] = key[0];
        return condition;
      }) as any[];

      return conditionsArr.map((c) => {
        let values = [c.value.toString()];
        let additionalValues = Object.entries(c.additionalValues).map((key) => key[1]['value'].toString());
        values = values.concat(additionalValues);
        return {
          Id: c.id.startsWith("NEW") ? null : c.id,
          TypeKey: c.typeKey,
          SubTypeKey: c.subTypeKey,
          ComparerKey: c.operatorKey,
          Values: values
        } as ErrorRuleCondition
      });
    }
    else {
      return [];
    }
  }
}
